import { useEffect, useState } from "react";
import { api, idr } from "../lib/api";
import { TrendingUp, Users, ShoppingBag, Award, Zap, ArrowUpRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { motion } from "framer-motion";
import { useCabangOptions } from "../lib/cabang";
import { MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { bulanIniISO } from "@/lib/tanggal";

export default function SalesReport() {
  const [periode, setPeriode] = useState(bulanIniISO());
  const [branch, setBranch] = useState("");
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const [data, setData] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(); if (periode) p.set("periode", periode); if (branch) p.set("branch", branch);
    api.get(`/sales/upselling?${p}`).then(r => setData(r.data));
  }, [periode, branch]);

  const chartData = (data?.per_sales || []).slice(0, 10).map(d => ({
    name: d.nama_penjual.split(" ")[0].slice(0, 10),
    fullName: d.nama_penjual,
    upselling: d.upselling_rate,
    single: d.single_item,
    multi: d.multi_item,
  }));

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Analytics</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Laporan Upselling Sales</h1>
        <p className="mt-2 text-sm text-slate-500">Rasio penjual yang berhasil jual &gt;1 item vs hanya 1 item — indikator utama upselling performance.</p>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode</span>
          <MonthPicker value={periode} onChange={setPeriode} data-testid="report-periode"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
      </div>

      <LoadingState loading={!data} data-testid="sales-report-loading">
        {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            <Kpi icon={ShoppingBag} label="Total Transaksi" value={data.summary.total_transaksi} tint="slate" />
            <Kpi icon={Zap} label="Multi-item (Upselling)" value={data.summary.total_multi_item} tint="emerald" sub={`${data.summary.overall_upselling_rate}% dari total`} />
            <Kpi icon={Users} label="Single-item" value={data.summary.total_single_item} tint="amber" />
            <Kpi icon={TrendingUp} label="Total Omzet" value={idr(data.summary.total_omzet)} tint="sky" />
          </div>

          {chartData.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6">
              <h3 className="font-display font-semibold text-slate-900 mb-1">Upselling Rate per Sales (Top 10)</h3>
              <p className="text-xs text-slate-500 mb-4">% transaksi &gt;1 item dari total transaksi masing-masing sales</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    formatter={(v, n, p) => [`${v}%`, "Upselling Rate"]}
                    labelFormatter={(_, p) => p[0]?.payload?.fullName}
                  />
                  <Bar dataKey="upselling" radius={[6, 6, 0, 0]}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.upselling >= 50 ? "#059669" : d.upselling >= 25 ? "#F59E0B" : "#EF4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Ranking Sales</h3>
              <p className="text-xs text-slate-500">Diurutkan berdasarkan upselling rate</p>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="text-left py-3 px-4">#</th>
                <th className="text-left py-3 px-4">Sales</th>
                <th className="text-left py-3 px-4">Branch</th>
                <th className="text-center py-3 px-4">Total Tx</th>
                <th className="text-center py-3 px-4">1 Item</th>
                <th className="text-center py-3 px-4">Multi Item</th>
                <th className="text-center py-3 px-4">Avg Item/Tx</th>
                <th className="text-right py-3 px-4">Upselling Rate</th>
                <th className="text-right py-3 px-4">Total Omzet</th>
              </tr></thead>
              <tbody>
                {data.per_sales.map((s, i) => (
                  <tr key={s.nama_penjual} data-testid={`upsell-${i}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4">
                      {i < 3 ? (
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-slate-200 text-slate-700" : "bg-orange-100 text-orange-800"
                        }`}><Award className="w-3.5 h-3.5" /></span>
                      ) : <span className="text-slate-400 font-mono">{i + 1}</span>}
                    </td>
                    <td className="py-3 px-4 font-medium text-slate-900">{s.nama_penjual}</td>
                    <td className="py-3 px-4 text-xs text-slate-500">{s.branches}</td>
                    <td className="py-3 px-4 text-center font-semibold">{s.total_transaksi}</td>
                    <td className="py-3 px-4 text-center text-slate-600">{s.single_item}</td>
                    <td className="py-3 px-4 text-center text-emerald-700 font-semibold">{s.multi_item}</td>
                    <td className="py-3 px-4 text-center font-mono">{s.avg_items}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-20 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full ${s.upselling_rate >= 50 ? "bg-emerald-600" : s.upselling_rate >= 25 ? "bg-amber-500" : "bg-rose-500"}`}
                            style={{ width: `${Math.min(100, s.upselling_rate)}%` }} />
                        </div>
                        <span className="font-semibold w-14 text-right">{s.upselling_rate}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono">{idr(s.total_omzet)}</td>
                  </tr>
                ))}
                {data.per_sales.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-sm text-slate-500">Belum ada data penjualan di periode ini</td></tr>}
              </tbody>
            </table>
          </div>
        </>
        )}
      </LoadingState>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint = "emerald", sub }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-4`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> {sub}</p>}
    </motion.div>
  );
}
